import apiService from './apiService';

// =====================================
// JOURNAL TYPES
// =====================================

export type JournalType = 'SALE' | 'PURCHASE' | 'CASH' | 'BANK' | 'GENERAL';

export interface Journal {
  id: string;
  name: string;
  code: string;
  type: JournalType;
  defaultAccountId?: string;
  companyId: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: {
    entries: number;
  };
}

export interface CreateJournalInput {
  name: string;
  code: string;
  type: JournalType;
  defaultAccountId?: string;
  companyId: string;
}

export interface UpdateJournalInput {
  name?: string;
  code?: string;
  type?: JournalType;
  defaultAccountId?: string;
  active?: boolean;
}

export interface JournalFilters {
  type?: JournalType;
  active?: boolean;
  search?: string;
}

export interface JournalStats {
  total: number;
  active: number;
  inactive: number;
  byType: Array<{
    type: string;
    count: number;
  }>;
}

// =====================================
// JOURNAL ENTRY TYPES
// =====================================

export type JournalEntryState = 'DRAFT' | 'POSTED' | 'CANCELLED';

export interface JournalEntryLine {
  id?: string;
  accountId: string;
  name?: string;
  debit: number;
  credit: number;
  partnerId?: string;
  currencyId?: string;
  amountCurrency?: number;
  analyticAccountId?: string;
  account?: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
}

export interface JournalEntry {
  id: string;
  name: string;
  journalId: string;
  date: Date | string;
  ref?: string;
  narration?: string;
  state: JournalEntryState;
  companyId: string;
  createdBy: string;
  postedBy?: string;
  postedAt?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  journal?: Journal;
  lines: JournalEntryLine[];
}

export interface CreateJournalEntryInput {
  journalId: string;
  date: Date | string;
  ref?: string;
  narration?: string;
  lines: Omit<JournalEntryLine, 'id' | 'account'>[];
}

export interface UpdateJournalEntryInput {
  journalId?: string;
  date?: Date | string;
  ref?: string;
  narration?: string;
  lines?: Omit<JournalEntryLine, 'id' | 'account'>[];
}

export interface JournalEntryFilters {
  journalId?: string;
  state?: JournalEntryState;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  search?: string;
  partnerId?: string;
}

export interface ReverseJournalEntryInput {
  reversalDate: Date | string;
  narration?: string;
}

export interface JournalEntryStats {
  total: number;
  draft: number;
  posted: number;
  cancelled: number;
}

export interface GeneralLedgerLine {
  id: string;
  entryId: string;
  accountId: string;
  name?: string;
  debit: number;
  credit: number;
  partnerId?: string;
  currencyId?: string;
  amountCurrency?: number;
  analyticAccountId?: string;
  reconciled: boolean;
  createdAt: string;
  account: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
  entry: {
    id: string;
    name: string;
    date: Date | string;
    ref?: string;
    narration?: string;
    journal: {
      id: string;
      name: string;
      code: string;
      type: string;
    };
  };
}

export interface GeneralLedgerFilters {
  accountId?: string;
  journalId?: string;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  partnerId?: string;
  reconciled?: boolean;
}

// =====================================
// JOURNAL SERVICE
// =====================================

class JournalService {
  // ============ JOURNAL ENDPOINTS ============

  /**
   * Get all journals with optional filters
   */
  async getJournals(filters?: JournalFilters): Promise<Journal[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await apiService.get(`/accounting/journals?${params.toString()}`);
    return response.data || [];
  }

  /**
   * Get only active journals
   */
  async getActiveJournals(): Promise<Journal[]> {
    const response = await apiService.get('/accounting/journals/active');
    return response.data || [];
  }

  /**
   * Get journals by type
   */
  async getJournalsByType(type: JournalType): Promise<Journal[]> {
    const response = await apiService.get(`/accounting/journals/type/${type}`);
    return response.data || [];
  }

  /**
   * Get journal by ID
   */
  async getJournalById(id: string): Promise<Journal> {
    const response = await apiService.get(`/accounting/journals/${id}`);
    return response.data;
  }

  /**
   * Get journal by code
   */
  async getJournalByCode(code: string): Promise<Journal> {
    const response = await apiService.get(`/accounting/journals/code/${code}`);
    return response.data;
  }

  /**
   * Create a new journal
   */
  async createJournal(data: CreateJournalInput): Promise<Journal> {
    const response = await apiService.post('/accounting/journals', data);
    return response.data;
  }

  /**
   * Update a journal
   */
  async updateJournal(id: string, data: UpdateJournalInput): Promise<Journal> {
    const response = await apiService.put(`/accounting/journals/${id}`, data);
    return response.data;
  }

  /**
   * Deactivate a journal (soft delete)
   */
  async deactivateJournal(id: string): Promise<Journal> {
    const response = await apiService.patch(`/accounting/journals/${id}/deactivate`);
    return response.data;
  }

  /**
   * Reactivate a journal
   */
  async reactivateJournal(id: string): Promise<Journal> {
    const response = await apiService.patch(`/accounting/journals/${id}/reactivate`);
    return response.data;
  }

  /**
   * Delete a journal (only if no entries)
   */
  async deleteJournal(id: string): Promise<void> {
    await apiService.delete(`/accounting/journals/${id}`);
  }

  /**
   * Get journal statistics
   */
  async getJournalStats(): Promise<JournalStats> {
    const response = await apiService.get('/accounting/journals/stats');
    return response.data;
  }

  // ============ JOURNAL ENTRY ENDPOINTS ============

  /**
   * Get all journal entries with optional filters
   */
  async getJournalEntries(filters?: JournalEntryFilters): Promise<JournalEntry[]> {
    const params = new URLSearchParams();
    if (filters?.journalId) params.append('journalId', filters.journalId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.dateFrom) params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo) params.append('dateTo', new Date(filters.dateTo).toISOString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);

    const response = await apiService.get(`/accounting/journal-entries?${params.toString()}`);
    return response.data || [];
  }

  /**
   * Get journal entry by ID
   */
  async getJournalEntryById(id: string): Promise<JournalEntry> {
    const response = await apiService.get(`/accounting/journal-entries/${id}`);
    return response.data;
  }

  /**
   * Get journal entry by name (entry number)
   */
  async getJournalEntryByName(name: string): Promise<JournalEntry> {
    const response = await apiService.get(`/accounting/journal-entries/name/${name}`);
    return response.data;
  }

  /**
   * Create a new journal entry
   */
  async createJournalEntry(data: CreateJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.post('/accounting/journal-entries', data);
    return response.data;
  }

  /**
   * Update a journal entry (only DRAFT entries)
   */
  async updateJournalEntry(id: string, data: UpdateJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.put(`/accounting/journal-entries/${id}`, data);
    return response.data;
  }

  /**
   * Post a journal entry (DRAFT → POSTED)
   */
  async postJournalEntry(id: string): Promise<JournalEntry> {
    const response = await apiService.post(`/accounting/journal-entries/${id}/post`, {});
    return response.data;
  }

  /**
   * Reverse a posted journal entry
   */
  async reverseJournalEntry(id: string, data: ReverseJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.post(`/accounting/journal-entries/${id}/reverse`, data);
    return response.data;
  }

  /**
   * Cancel a journal entry
   */
  async cancelJournalEntry(id: string): Promise<JournalEntry> {
    const response = await apiService.patch(`/accounting/journal-entries/${id}/cancel`, {});
    return response.data;
  }

  /**
   * Delete a journal entry (only DRAFT entries)
   */
  async deleteJournalEntry(id: string): Promise<void> {
    await apiService.delete(`/accounting/journal-entries/${id}`);
  }

  /**
   * Get journal entry statistics
   */
  async getJournalEntryStats(): Promise<JournalEntryStats> {
    const response = await apiService.get('/accounting/journal-entries/stats');
    return response.data;
  }

  // ============ GENERAL LEDGER ENDPOINTS ============

  /**
   * Get general ledger (all posted entry lines)
   */
  async getGeneralLedger(filters?: GeneralLedgerFilters): Promise<GeneralLedgerLine[]> {
    const params = new URLSearchParams();
    if (filters?.accountId) params.append('accountId', filters.accountId);
    if (filters?.journalId) params.append('journalId', filters.journalId);
    if (filters?.dateFrom) params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo) params.append('dateTo', new Date(filters.dateTo).toISOString());
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    if (filters?.reconciled !== undefined) params.append('reconciled', filters.reconciled.toString());

    const response = await apiService.get(`/accounting/general-ledger?${params.toString()}`);
    return response.data || [];
  }

  /**
   * Calculate account balance from posted entries
   */
  async getAccountBalanceFromEntries(accountId: string, upToDate?: Date | string): Promise<number> {
    const params = new URLSearchParams();
    if (upToDate) params.append('upToDate', new Date(upToDate).toISOString());

    const response = await apiService.get(`/accounting/accounts/${accountId}/balance?${params.toString()}`);
    return response.data.balance || 0;
  }

  // ============ VALIDATION HELPERS ============

  /**
   * Validate double-entry (debit must equal credit)
   */
  validateDoubleEntry(lines: JournalEntryLine[]): { valid: boolean; message?: string } {
    if (lines.length < 2) {
      return { valid: false, message: 'Journal entry must have at least 2 lines' };
    }

    const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
    const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);

    const difference = Math.abs(totalDebit - totalCredit);
    if (difference > 0.01) {
      return {
        valid: false,
        message: `Debit ($${totalDebit.toFixed(2)}) must equal Credit ($${totalCredit.toFixed(2)}). Difference: $${difference.toFixed(2)}`
      };
    }

    // Check that each line has either debit or credit, but not both
    for (const line of lines) {
      const debit = Number(line.debit);
      const credit = Number(line.credit);

      if (debit > 0 && credit > 0) {
        return {
          valid: false,
          message: 'Each line must have either debit or credit, not both'
        };
      }
      if (debit === 0 && credit === 0) {
        return {
          valid: false,
          message: 'Each line must have either debit or credit amount'
        };
      }
    }

    return { valid: true };
  }

  /**
   * Calculate totals for journal entry lines
   */
  calculateTotals(lines: JournalEntryLine[]): { totalDebit: number; totalCredit: number } {
    const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
    const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);
    return { totalDebit, totalCredit };
  }
}

export const journalService = new JournalService();
export default journalService;